import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useProperty,
  usePropertyField,
  useCollectionBinding,
  usePage,
  useEditMode,
  useSocialLinks,
  EditableToken,
  EditableSlotGroup,
  EditableCollectionBinding,
  RichTextContent,
} from '@vibesites/runtime/react';
import { FacebookIcon, InstagramIcon } from '@vibesites/assets';
import pagesData from '../header-nav.json';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { OPEN_APPLY_MODAL_EVENT } from '../utils/applyModal';
import {
  OPEN_SCHEDULE_TOUR_MODAL_EVENT,
  SCHEDULE_TOUR_HASH,
  clearLegacyScheduleTourModalPending,
  isScheduleTourHref,
  isScheduleTourLandingPath,
} from '../utils/scheduleTourModal';
import { emitMobileMenuChange } from '../utils/mobileMenu';
import { btnOnDark, btnOnLight } from '../utils/buttonStyles';

// ============================================================================
// Dynamic Navigation from pages.json
// ============================================================================

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  template?: string;
  noindex?: boolean;
  /** When set, this page is rendered as a sub-nav item under the parent
      page's nav entry (a dropdown on desktop, indented row on mobile). */
  parent?: string;
}

/**
 * Page aliases excluded from the main navigation. Home is intentionally
 * kept (unlike the parent Rise header) so the bar matches the Ascent-style
 * reference: Home sits as the first link with an active underline.
 */
const NAV_EXCLUDED_ALIASES = new Set([
  'privacy-policy',
  'terms-and-conditions',
  'sitemap',
  'accessibility',
  'apply-now',
  'thank-you',
  'resident-access',
  'schedule-a-tour',
  'hyperlocal',
  'reviews',
  'pet-policy',
]);

interface NavLink {
  label: string;
  href: string;
  /** Sub-nav items rendered as a dropdown on desktop and indented rows on
      mobile. Populated when other pages declare `parent: <this alias>`. */
  children?: NavLink[];
}

interface VirtualTour {
  virtual_tour_url?: string | null;
}

/**
 * Build navigation links from pages.json.
 * Only includes pages that are visible and not in the exclusion list.
 * Sorted by position. Pages declaring `parent: <alias>` are nested as
 * `children` of their parent entry rather than appearing at the top level.
 * Orphaned children (parent missing or hidden) fall back to the top level
 * so a page is never silently dropped from the nav.
 */
function buildNavLinks(hasVirtualTours: boolean, hasInteractiveLeasing: boolean): NavLink[] {
  const eligible = Object.entries(pagesData as Record<string, PageEntry>)
    .filter(([alias, page]) => {
      if (NAV_EXCLUDED_ALIASES.has(alias)) return false;
      if (page.visible === false) return false;
      if (page.template) return false;
      if (alias === 'virtual-tour' && !hasVirtualTours) return false;
      if (alias === 'interactive-map' && !hasInteractiveLeasing) return false;
      return true;
    })
    .sort(([, a], [, b]) => a.position - b.position);

  const eligibleAliases = new Set(eligible.map(([alias]) => alias));
  const linksByAlias = new Map<string, NavLink>();
  const topLevel: NavLink[] = [];

  for (const [alias, page] of eligible) {
    linksByAlias.set(alias, { label: page.name, href: page.path });
  }

  for (const [alias, page] of eligible) {
    const link = linksByAlias.get(alias)!;
    if (page.parent && eligibleAliases.has(page.parent) && page.parent !== alias) {
      const parentLink = linksByAlias.get(page.parent)!;
      (parentLink.children ??= []).push(link);
    } else {
      topLevel.push(link);
    }
  }

  return topLevel;
}

function normalizePath(path: string | undefined | null): string {
  if (!path) return '/';
  if (path === '/') return '/';
  return path.endsWith('/') ? path.slice(0, -1) : path;
}

function isActivePath(href: string, currentPath: string): boolean {
  const a = normalizePath(href);
  const b = normalizePath(currentPath);
  return a === b;
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_primary_logo', suggestedFields: ['property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const applyNowLinkDef: SlotGroupDefinition = {
  label: 'Apply Now Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const residentAccessLinkDef: SlotGroupDefinition = {
  label: 'Resident Login Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Login' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// SVG Icon Components
// ============================================================================

/** Classic diagonal handset silhouette (earpiece top-left → mouthpiece bottom-right). */
function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 512 512" fill="currentColor" aria-hidden="true">
      {/* Default FA orientation: earpiece bottom-left / mouthpiece top-right */}
      <path d="M164.9 24.6c-7.7-18.6-28-28.5-47.4-23.2l-88 24C12.1 30.2 0 46 0 64 0 311.4 200.6 512 448 512c18 0 33.8-12.1 38.6-29.5l24-88c5.3-19.4-4.6-39.7-23.2-47.4l-96-40c-16.3-6.8-35.2-2.1-46.3 11.6L304.7 368C234.3 334.7 177.3 277.7 144 207.3L193.3 167c13.7-11.2 18.4-30 11.6-46.3l-40-96z" />
    </svg>
  );
}

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function formatTime(raw: string): string {
  const trimmed = raw.trim();
  if (/[ap]m/i.test(trimmed)) {
    return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  }
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;

  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_SHORT_LABELS: Record<string, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sunday',
};

interface OfficeHourEntry {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

interface GroupedHours {
  shortLabel: string;
  hours: string;
}

function hasHoursValue(v: string | null | undefined): boolean {
  return !!v && v.trim().length > 0;
}

function normalizeDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

function groupOfficeHours(raw: OfficeHourEntry[]): GroupedHours[] {
  const groups: GroupedHours[] = [];

  for (const key of DAY_ORDER) {
    const entry: OfficeHourEntry =
      raw.find((e) => hasHoursValue(e.day) && normalizeDay(e.day || '') === key) ?? {};
    const dayShortLabel = DAY_SHORT_LABELS[key];
    const isClosed = !hasHoursValue(entry.open_time) || !hasHoursValue(entry.close_time);
    const hoursStr = isClosed
      ? 'Closed'
      : `${formatTime(entry.open_time || '')} - ${formatTime(entry.close_time || '')}`;

    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hoursStr) {
      const shortDash = prev.shortLabel.indexOf(' - ');
      const startShort = shortDash !== -1 ? prev.shortLabel.slice(0, shortDash) : prev.shortLabel;
      prev.shortLabel = `${startShort} - ${dayShortLabel}`;
    } else {
      groups.push({ shortLabel: dayShortLabel, hours: hoursStr });
    }
  }

  return groups;
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

// ============================================================================
// Desktop Nav Link
// ============================================================================

function DesktopNavLink({
  link,
  currentPath,
}: {
  link: NavLink;
  currentPath: string;
}) {
  const hasChildren = !!(link.children && link.children.length);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const active = isActivePath(link.href, currentPath);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  if (!hasChildren) {
    return (
      <a
        href={link.href}
        aria-current={active ? 'page' : undefined}
        className={`dolben-nav-link font-body text-[14px] font-normal text-[var(--vibe-color-text-on-primary)] transition-opacity py-2 whitespace-nowrap${active ? ' is-active' : ''}`}
      >
        {link.label}
      </a>
    );
  }

  return (
    <div
      ref={wrapperRef}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <a
        href={link.href}
        aria-current={active ? 'page' : undefined}
        className={`dolben-nav-link font-body text-[14px] font-normal text-[var(--vibe-color-text-on-primary)] transition-opacity py-2 flex items-center gap-1 whitespace-nowrap${active ? ' is-active' : ''}`}
        aria-haspopup="true"
        aria-expanded={open}
        onFocus={() => setOpen(true)}
      >
        {link.label}
        <ChevronDownIcon
          className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </a>

      <div
        className={`nav-more-dropdown absolute left-0 top-full mt-1 min-w-[200px] z-[70] transition-all duration-200 ${
          open
            ? 'opacity-100 visible translate-y-0'
            : 'opacity-0 invisible -translate-y-1'
        }`}
        style={{
          backgroundColor: 'var(--vibe-color-primary)',
          border: '1px solid color-mix(in srgb, var(--vibe-color-text-on-primary) 20%, transparent)',
        }}
      >
        {link.children!.map((child) => {
          const childActive = isActivePath(child.href, currentPath);
          return (
            <a
              key={child.href}
              href={child.href}
              aria-current={childActive ? 'page' : undefined}
              className={`dolben-nav-link block px-5 py-3 font-body text-[14px] font-normal text-[var(--vibe-color-text-on-primary)] transition-opacity${childActive ? ' is-active' : ''}`}
            >
              {child.label}
            </a>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// Desktop Navigation with dynamic "More" overflow dropdown
// ============================================================================

function DesktopNav({
  navLinks,
  currentPath,
}: {
  navLinks: NavLink[];
  currentPath: string;
}) {
  const navContainerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(navLinks.length);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  const recalculate = useCallback(() => {
    const container = navContainerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const availableWidth = container.offsetWidth;
    const children = Array.from(measure.children) as HTMLElement[];
    const computedGap = parseFloat(getComputedStyle(container).gap) || 28;
    const moreButtonWidth = 90;

    let totalWidth = 0;
    let fitCount = 0;

    for (let i = 0; i < children.length; i++) {
      const childWidth = children[i].offsetWidth;
      const widthWithGap = i === 0 ? childWidth : childWidth + computedGap;
      const needed = totalWidth + widthWithGap + (i < children.length - 1 ? moreButtonWidth + computedGap : 0);

      if (needed > availableWidth && i < children.length) {
        const remainingWidth = totalWidth + widthWithGap;
        let allFit = true;
        let testWidth = remainingWidth;
        for (let j = i + 1; j < children.length; j++) {
          testWidth += children[j].offsetWidth + computedGap;
          if (testWidth > availableWidth) {
            allFit = false;
            break;
          }
        }
        if (allFit) {
          fitCount = children.length;
        } else {
          fitCount = i;
        }
        break;
      }
      totalWidth += widthWithGap;
      fitCount = i + 1;
    }

    setVisibleCount(fitCount);
  }, [navLinks.length]);

  useEffect(() => {
    recalculate();
    const observer = new ResizeObserver(() => recalculate());
    if (navContainerRef.current) {
      observer.observe(navContainerRef.current);
    }
    return () => observer.disconnect();
  }, [recalculate]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
    }
    if (moreOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [moreOpen]);

  const visibleLinks = navLinks.slice(0, visibleCount);
  const overflowLinks = navLinks.slice(visibleCount);

  return (
    <>
      <div
        ref={measureRef}
        aria-hidden="true"
        className="flex items-center gap-7 absolute pointer-events-none"
        style={{ visibility: 'hidden', position: 'absolute', top: 0, left: 0, whiteSpace: 'nowrap' }}
      >
        {navLinks.map((link) => (
          <span
            key={link.href}
            className="font-body text-[14px] font-normal inline-flex items-center gap-1"
          >
            {link.label}
            {link.children && link.children.length ? (
              <ChevronDownIcon className="w-3.5 h-3.5" />
            ) : null}
          </span>
        ))}
      </div>

      <nav
        ref={navContainerRef}
        className="hidden lg:flex items-center justify-end gap-6 xl:gap-7 flex-1 min-w-0 ml-8"
        role="navigation"
        aria-label="Main navigation"
      >
        {visibleLinks.map((link) => (
          <DesktopNavLink key={link.href} link={link} currentPath={currentPath} />
        ))}

        {overflowLinks.length > 0 && (
          <div
            ref={moreRef}
            className="relative"
            onMouseEnter={() => setMoreOpen(true)}
            onMouseLeave={() => setMoreOpen(false)}
          >
            <button
              onClick={() => setMoreOpen(!moreOpen)}
              onFocus={() => setMoreOpen(true)}
              className="dolben-nav-link font-body text-[14px] font-normal text-[var(--vibe-color-text-on-primary)] transition-opacity py-2 flex items-center gap-1 whitespace-nowrap"
              aria-expanded={moreOpen}
              aria-haspopup="true"
            >
              More
              <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform duration-200 ${moreOpen ? 'rotate-180' : ''}`} />
            </button>

            <div
              className={`nav-more-dropdown absolute right-0 top-full mt-1 min-w-[200px] z-[70] transition-all duration-200 ${
                moreOpen
                  ? 'opacity-100 visible translate-y-0'
                  : 'opacity-0 invisible -translate-y-1'
              }`}
              style={{
                backgroundColor: 'var(--vibe-color-primary)',
                border: '1px solid color-mix(in srgb, var(--vibe-color-text-on-primary) 20%, transparent)',
              }}
            >
              {overflowLinks.map((link) => (
                <React.Fragment key={link.href}>
                  <a
                    href={link.href}
                    aria-current={isActivePath(link.href, currentPath) ? 'page' : undefined}
                    className={`dolben-nav-link block px-5 py-3 font-body text-[14px] font-normal text-[var(--vibe-color-text-on-primary)] transition-opacity${isActivePath(link.href, currentPath) ? ' is-active' : ''}`}
                  >
                    {link.label}
                  </a>
                  {link.children?.map((child) => (
                    <a
                      key={child.href}
                      href={child.href}
                      aria-current={isActivePath(child.href, currentPath) ? 'page' : undefined}
                      className={`dolben-nav-link block pl-9 pr-5 py-3 font-body text-[14px] font-normal text-[var(--vibe-color-text-on-primary)]/80 transition-opacity${isActivePath(child.href, currentPath) ? ' is-active' : ''}`}
                    >
                      {child.label}
                    </a>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </nav>
    </>
  );
}

// ============================================================================
// Mobile Nav Link
// ============================================================================

function MobileNavLink({
  link,
  onNavigate,
  currentPath,
  isChild,
}: {
  link: NavLink;
  onNavigate: () => void;
  currentPath: string;
  isChild?: boolean;
}) {
  const active = isActivePath(link.href, currentPath);
  return (
    <a
      href={link.href}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      className={`mobile-nav-link font-body text-[17px] font-normal leading-none text-[var(--vibe-color-text-on-primary)] transition-opacity${
        active ? ' is-active' : ''
      }${isChild ? ' is-child' : ''}`}
    >
      {link.label}
    </a>
  );
}

// ============================================================================
// Main Header Component
// ============================================================================
// STRUCTURE:
// Desktop — solid two-row header (utility + main nav).
// Mobile  — phone icon | centered white logo | burger; transparent over the
//           hero, primary fill after scroll. Open menu is a full-screen
//           overlay matching the Ascent reference (logo, phone, links,
//           ghost CTAs, office hours).
// ============================================================================

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [applyModalOpen, setApplyModalOpen] = useState(false);
  const [scheduleTourModalOpen, setScheduleTourModalOpen] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const headerRef = useRef<HTMLElement>(null);

  // Mobile transparent → primary transition
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const mobileSolid = scrolled || menuOpen;

  const pageCtx = usePage();
  const currentPath = useMemo(() => {
    if (pageCtx?.path) return pageCtx.path;
    if (typeof window !== 'undefined') return window.location.pathname;
    return '/';
  }, [pageCtx?.path]);

  const virtualTours = useCollectionBinding<VirtualTour>('header.virtual_tours', { collection: 'virtual_tours' });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  const propertyForLeasing = useProperty() as Record<string, unknown>;
  const apartmentSyncJsId = propertyForLeasing?.custom_apartmentsync_javascript_id as
    | string
    | null
    | undefined;
  const apartmentSyncConfigId = propertyForLeasing?.custom_apartmentsync_config_id as
    | string
    | null
    | undefined;

  const interactiveMapShowIfExists = useContentToken('header.interactive_map_show_if_exists');
  const hasInteractiveMapOverride = !!(
    interactiveMapShowIfExists && String(interactiveMapShowIfExists).trim()
  );

  const hasInteractiveLeasing =
    (!!(apartmentSyncJsId && String(apartmentSyncJsId).trim()) &&
      !!(apartmentSyncConfigId && String(apartmentSyncConfigId).trim())) ||
    hasInteractiveMapOverride;

  const navLinks = useMemo(
    () => buildNavLinks(hasVirtualTours, hasInteractiveLeasing),
    [hasVirtualTours, hasInteractiveLeasing],
  );

  const phone = useContentToken('header.phone');

  const logoRaw = useSlotGroup('header.logo', logoSlotDef) as {
    image?: string | null;
    alt?: string | null;
  };
  const monoWhiteLogo = usePropertyField('property_mono_white_logo');
  // Solid black header always — prefer mono white so the logo reads on #000.
  const logoImage = monoWhiteLogo || logoRaw.image;
  const logo = { ...logoRaw, image: logoImage };

  const applyNowLink = useSlotGroup('header.apply_now_link', applyNowLinkDef);
  const residentAccessLink = useSlotGroup('header.resident_access_link', residentAccessLinkDef);
  const scheduleTourEmbedUrl = useContentToken('header.schedule_tour_embed_url');

  const officeHours = useCollectionBinding<OfficeHourEntry>('header.office_hours', {
    collection: 'office_hours',
  });
  const groupedHours = useMemo(
    () => (officeHours && officeHours.length > 0 ? groupOfficeHours(officeHours) : []),
    [officeHours],
  );

  const openApplyModal = useCallback((e?: React.MouseEvent) => {
    e?.preventDefault();
    setMenuOpen(false);
    setApplyModalOpen(true);
  }, []);

  const closeApplyModal = useCallback(() => setApplyModalOpen(false), []);

  const closeScheduleTourModal = useCallback(() => setScheduleTourModalOpen(false), []);

  // Let page-level Apply CTAs (e.g. ApplyCtaBanner) open this same popup.
  useEffect(() => {
    const onOpen = () => {
      setMenuOpen(false);
      setApplyModalOpen(true);
    };
    window.addEventListener(OPEN_APPLY_MODAL_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_APPLY_MODAL_EVENT, onOpen);
  }, []);

  // Let page-level Schedule a Tour CTAs open this same popup.
  useEffect(() => {
    const onOpen = () => {
      setMenuOpen(false);
      setScheduleTourModalOpen(true);
    };
    window.addEventListener(OPEN_SCHEDULE_TOUR_MODAL_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_SCHEDULE_TOUR_MODAL_EVENT, onOpen);
  }, []);

  const editMode = useEditMode();
  const isEditorPreview = !!editMode;
  const isEditing = !!editMode?.isEditMode;

  // Any tour link (CTA, rich-text cross-link, block) opens the popup in place —
  // never navigate away from the page the user is currently on. Window capture
  // runs ahead of every other click handler. While editing, clicks must reach
  // the inline editor's selection handlers instead.
  useEffect(() => {
    if (isEditing) return;
    const onClick = (e: MouseEvent) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (e.button !== 0) return;
      const anchor = (e.target as Element | null)?.closest?.(
        'a[href]',
      ) as HTMLAnchorElement | null;
      if (!anchor) return;
      if (!isScheduleTourHref(anchor.getAttribute('href'))) return;
      e.preventDefault();
      e.stopPropagation();
      setMenuOpen(false);
      setScheduleTourModalOpen(true);
    };
    window.addEventListener('click', onClick, true);
    return () => window.removeEventListener('click', onClick, true);
  }, [isEditing]);

  // Clear a leftover auto-open flag from an older redirect flow so the popup
  // never appears on load — only when a Schedule a Tour CTA is clicked.
  useEffect(() => {
    clearLegacyScheduleTourModalPending();
  }, []);

  // A tour CTA clicked before hydration only sets the in-page hash; open the
  // popup for it and drop the hash so the URL stays on the current page.
  useEffect(() => {
    if (isEditing) return;
    const openFromHash = () => {
      if (window.location.hash !== SCHEDULE_TOUR_HASH) return;
      window.history.replaceState(
        window.history.state,
        '',
        window.location.pathname + window.location.search,
      );
      setMenuOpen(false);
      setScheduleTourModalOpen(true);
    };
    openFromHash();
    window.addEventListener('hashchange', openFromHash);
    return () => window.removeEventListener('hashchange', openFromHash);
  }, [isEditing]);

  // /schedule-a-tour and /tour are not real pages. Old bookmarks or external
  // links get sent to the homepage with the popup open instead.
  useEffect(() => {
    if (isEditorPreview) return;
    if (
      !isScheduleTourLandingPath(currentPath) &&
      !isScheduleTourLandingPath(window.location.pathname)
    ) {
      return;
    }
    window.location.replace('/' + SCHEDULE_TOUR_HASH);
  }, [currentPath, isEditorPreview]);
  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="lexend-deca"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Lexend+Deca:wght@300;400;500;600;700&display=swap';
    link.setAttribute('data-vibe-font', 'lexend-deca');
    document.head.appendChild(link);
  }, []);

  const updateHeaderHeight = useCallback(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight);
    }
  }, []);

  useEffect(() => {
    updateHeaderHeight();
    const observer = new ResizeObserver(updateHeaderHeight);
    if (headerRef.current) {
      observer.observe(headerRef.current);
    }
    return () => observer.disconnect();
  }, [updateHeaderHeight]);

  useEffect(() => {
    document.body.style.overflow =
      menuOpen || applyModalOpen || scheduleTourModalOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen, applyModalOpen, scheduleTourModalOpen]);

  useEffect(() => {
    if (!applyModalOpen && !scheduleTourModalOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (scheduleTourModalOpen) closeScheduleTourModal();
      else if (applyModalOpen) closeApplyModal();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [applyModalOpen, scheduleTourModalOpen, closeApplyModal, closeScheduleTourModal]);

  const closeMobileMenu = () => setMenuOpen(false);

  useEffect(() => {
    emitMobileMenuChange(menuOpen);
  }, [menuOpen]);

  const social = useSocialLinks();
  const socialLinks = [
    social.facebook
      ? { href: social.facebook, label: 'Facebook', Icon: FacebookIcon }
      : null,
    social.instagram
      ? { href: social.instagram, label: 'Instagram', Icon: InstagramIcon }
      : null,
  ].filter(Boolean) as Array<{
    href: string;
    label: string;
    Icon: typeof FacebookIcon;
  }>;

  return (
    <>
      {/* Desktop spacer only — mobile header overlays the hero */}
      <div
        aria-hidden="true"
        className="hidden lg:block"
        style={{ height: headerHeight || undefined }}
      />

      <header
        ref={headerRef}
        className={`fixed left-0 right-0 z-50 w-full dolben-header${mobileSolid ? ' header-mobile-solid' : ' header-mobile-transparent'}`}
        style={{ top: 0 }}
      >
        <EditableToken
          path="header.interactive_map_show_if_exists"
          type="text"
          label="Interactive Map: Show If Exists"
          description="Expose the Interactive Map page in the navigation if this token or field contains a value (e.g. custom_engrain_sightmap_url). Leave blank to hide."
          as="span"
          className="sr-only"
        >
          {interactiveMapShowIfExists || ''}
        </EditableToken>
        <EditableToken
          path="header.schedule_tour_embed_url"
          type="url"
          label="Schedule Tour Embed URL"
          description="Iframe URL for the Schedule a Tour popup. Bound to custom_mri_tour_scheduler_url by default."
          as="span"
          className="sr-only"
        >
          {scheduleTourEmbedUrl || ''}
        </EditableToken>

        <div
          className="header-bar"
          style={{ paddingTop: 'var(--offer-banner-height, 0px)', transition: 'padding-top 0.5s cubic-bezier(0.4, 0, 0.2, 1)' }}
        >
          {/* ============================================================ */}
          {/* Utility row — secondary background                           */}
          {/* ============================================================ */}
          <div
            className="header-util-row hidden lg:block"
            style={{ backgroundColor: 'var(--vibe-color-secondary)', color: 'var(--vibe-color-text-on-secondary)' }}
          >
            <div className="px-6 xl:px-10">
              <div className="flex items-center justify-between gap-4 pt-4 pb-2">
                <a
                  href={`tel:${phone?.replace(/[^+\d]/g, '')}`}
                  className="dniphone dniphonehref header-util-phone"
                  aria-label="Call us"
                >
                  <EditableToken
                    path="header.phone"
                    defaultField="property_call_tracking_phone"
                    defaultValue="(555) 555-5555"
                    as="span"
                  >
                    {formatPhone(phone)}
                  </EditableToken>
                </a>

                <div className="flex items-center gap-8">
                  <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                    <button
                      type="button"
                      onClick={openApplyModal}
                      className="header-util-link"
                    >
                      {applyNowLink.label || 'Apply Now'}
                    </button>
                  </EditableSlotGroup>

                  <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                    <a
                      href={residentAccessLink.url || '/resident-access'}
                      target={residentAccessLink._target || '_self'}
                      className="header-util-link"
                    >
                      {residentAccessLink.label || 'Resident Login'}
                    </a>
                  </EditableSlotGroup>
                </div>
              </div>
            </div>
          </div>

          {/* ============================================================ */}
          {/* Main row — primary on desktop; transparent → primary on mobile */}
          {/* ============================================================ */}
          <div
            className="header-main-row"
            style={{ color: 'var(--vibe-color-text-on-primary)' }}
          >
            <div className="px-4 sm:px-6 xl:px-10">
              <div className="relative flex items-center min-h-[64px] lg:min-h-[88px] py-3 lg:justify-between">
                {/* Mobile: phone icon (left) — white rounded square; hidden while menu overlay is open */}
                <a
                  href={`tel:${phone?.replace(/[^+\d]/g, '')}`}
                  className={`dniphone dniphonehref header-mobile-phone relative z-[70] flex items-center justify-center w-9 h-9 flex-shrink-0 lg:hidden${
                    menuOpen ? ' invisible pointer-events-none' : ''
                  }`}
                  aria-label="Call us"
                  aria-hidden={menuOpen}
                  tabIndex={menuOpen ? -1 : undefined}
                >
                  <PhoneIcon className="w-[18px] h-[18px]" />
                </a>

                {/* Logo — centered on mobile, left-aligned on desktop; hidden in bar while overlay shows its own */}
                <div
                  className={`absolute left-1/2 top-1/2 z-[1] -translate-x-1/2 -translate-y-1/2 lg:static lg:z-auto lg:translate-x-0 lg:translate-y-0 lg:flex-shrink-0${
                    menuOpen ? ' lg:block max-lg:invisible max-lg:pointer-events-none' : ''
                  }`}
                >
                  <EditableSlotGroup groupPath="header.logo" definition={logoSlotDef}>
                    <a href="/" className="block">
                      {logo.image ? (
                        <VibesitesImage
                          url={logo.image}
                          alt={logo.alt || 'Community Logo'}
                          className="h-10 lg:h-14 w-auto object-contain"
                          sizes="180px"
                        />
                      ) : (
                        <div className="font-heading text-xl lg:text-2xl font-bold text-[var(--vibe-color-text-on-primary)] tracking-wide uppercase">
                          Dolben
                        </div>
                      )}
                    </a>
                  </EditableSlotGroup>
                </div>

                <DesktopNav navLinks={navLinks} currentPath={currentPath} />

                {/* Mobile: hamburger / close (right) — stays above the overlay */}
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  className="relative z-[70] w-10 h-10 flex items-center justify-center flex-shrink-0 ml-auto lg:ml-0 lg:hidden"
                  aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                  aria-expanded={menuOpen}
                >
                  <span
                    className={`absolute block w-6 h-0.5 bg-[var(--vibe-color-text-on-primary)] transition-all duration-300 ${
                      menuOpen ? 'rotate-45' : '-translate-y-2'
                    }`}
                  />
                  <span
                    className={`absolute block w-6 h-0.5 bg-[var(--vibe-color-text-on-primary)] transition-all duration-300 ${
                      menuOpen ? 'opacity-0' : ''
                    }`}
                  />
                  <span
                    className={`absolute block w-6 h-0.5 bg-[var(--vibe-color-text-on-primary)] transition-all duration-300 ${
                      menuOpen ? '-rotate-45' : 'translate-y-2'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ============================================================== */}
        {/* Mobile Menu Overlay — full-screen Ascent-style panel           */}
        {/* ============================================================== */}
        <div
          className={`mobile-menu-panel lg:hidden fixed inset-0 overflow-y-auto transition-all duration-300 z-[61] ${
            menuOpen
              ? 'opacity-100 visible'
              : 'opacity-0 invisible pointer-events-none'
          }`}
          style={{
            backgroundColor: 'var(--vibe-color-primary)',
            color: 'var(--vibe-color-text-on-primary)',
          }}
          aria-hidden={!menuOpen}
        >
          <div className="mobile-menu-inner flex min-h-full flex-col px-8 pb-10 pt-16">
            {/* Brand + phone */}
            <div className="flex flex-col items-center text-center">
              <EditableSlotGroup groupPath="header.logo" definition={logoSlotDef}>
                <a href="/" onClick={closeMobileMenu} className="block">
                  {logo.image ? (
                    <VibesitesImage
                      url={logo.image}
                      alt={logo.alt || 'Community Logo'}
                      className="h-14 w-auto object-contain mx-auto"
                      sizes="200px"
                    />
                  ) : (
                    <div className="font-heading text-2xl font-bold text-[var(--vibe-color-text-on-primary)] tracking-wide uppercase">
                      Dolben
                    </div>
                  )}
                </a>
              </EditableSlotGroup>

              <a
                href={`tel:${phone?.replace(/[^+\d]/g, '')}`}
                className="dniphone dniphonehref mobile-menu-phone mt-5 font-body text-[15px] font-normal text-[var(--vibe-color-text-on-primary)]"
              >
                <EditableToken
                  path="header.phone"
                  defaultField="property_call_tracking_phone"
                  defaultValue="(555) 555-5555"
                  as="span"
                >
                  {formatPhone(phone)}
                </EditableToken>
              </a>
            </div>

            <div className="mobile-menu-rule mt-6" aria-hidden="true" />

            {/* Nav links */}
            <nav
              role="navigation"
              aria-label="Mobile navigation"
              className="mobile-menu-nav flex flex-col gap-5 pt-8 pb-8"
            >
              {navLinks.map((link) => (
                <React.Fragment key={link.href}>
                  <MobileNavLink link={link} onNavigate={closeMobileMenu} currentPath={currentPath} />
                  {link.children?.map((child) => (
                    <MobileNavLink
                      key={child.href}
                      link={child}
                      onNavigate={closeMobileMenu}
                      currentPath={currentPath}
                      isChild
                    />
                  ))}
                </React.Fragment>
              ))}
            </nav>

            {/* Ghost CTAs */}
            <div className="flex flex-col gap-3">
              <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                <button
                  type="button"
                  onClick={openApplyModal}
                  className={`mobile-menu-cta block text-center w-full py-3.5 text-[13px] font-medium tracking-[0.14em] uppercase cursor-pointer ${btnOnDark}`}
                >
                  {applyNowLink.label || 'Apply Now'}
                </button>
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                <a
                  href={residentAccessLink.url || '/resident-access'}
                  target={residentAccessLink._target || '_self'}
                  onClick={closeMobileMenu}
                  className={`mobile-menu-cta block text-center w-full py-3.5 text-[13px] font-medium tracking-[0.14em] uppercase ${btnOnDark}`}
                >
                  {residentAccessLink.label || 'Resident Login'}
                </a>
              </EditableSlotGroup>
            </div>

            <div className="mobile-menu-rule mt-8" aria-hidden="true" />

            {/* Office hours */}
            <div className="pt-6">
              <EditableCollectionBinding bindingPath="header.office_hours" label="Office Hours">
                {groupedHours.length > 0 ? (
                  <dl className="mobile-menu-hours space-y-2.5">
                    {groupedHours.map((group, index) => (
                      <div
                        key={index}
                        className="flex justify-between gap-4 font-body text-[14px] text-[var(--vibe-color-text-on-primary)]"
                      >
                        <dt>{group.shortLabel}</dt>
                        <dd className="text-right whitespace-nowrap">{group.hours}</dd>
                      </div>
                    ))}
                  </dl>
                ) : (
                  <p className="font-body text-sm text-[var(--vibe-color-text-on-primary)]/50">
                    Hours coming soon
                  </p>
                )}
              </EditableCollectionBinding>
            </div>

            {socialLinks.length > 0 ? (
              <>
                <div className="mobile-menu-rule mt-8" aria-hidden="true" />
                <div
                  className="flex items-center justify-start gap-5 pt-6"
                  aria-label="Social media"
                >
                  {socialLinks.map(({ href, label, Icon }) => (
                    <a
                      key={label}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                      className="text-[var(--vibe-color-text-on-primary)] hover:opacity-70 transition-opacity"
                    >
                      <Icon size={22} color="currentColor" />
                    </a>
                  ))}
                </div>
              </>
            ) : null}
          </div>
        </div>
      </header>

      {applyModalOpen && (
        <div
          className="apply-leave-overlay"
          onClick={closeApplyModal}
          role="presentation"
        >
          <div
            className="apply-leave-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Application leave-site notice"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="apply-leave-close"
              onClick={closeApplyModal}
              aria-label="Close"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M2 2L14 14M14 2L2 14" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
              </svg>
            </button>

            <RichTextContent
              path="header.apply_modal_title"
              as="h2"
              className="apply-leave-title"
            />

            <RichTextContent
              path="header.apply_modal_intro"
              as="p"
              className="apply-leave-intro"
            />

            <ul className="apply-leave-list">
              <li>
                <RichTextContent path="header.apply_modal_bullet_1" as="span" />
              </li>
              <li>
                <RichTextContent path="header.apply_modal_bullet_2" as="span" />
              </li>
            </ul>

            <RichTextContent
              path="header.apply_modal_closing"
              as="p"
              className="apply-leave-closing"
            />

            <a
              href={applyNowLink.url || '/apply-now'}
              target={applyNowLink._target || '_blank'}
              rel={
                (applyNowLink._target || '_blank') === '_blank'
                  ? 'noopener noreferrer'
                  : undefined
              }
              className={`apply-leave-cta ${btnOnLight}`}
              onClick={closeApplyModal}
            >
              <RichTextContent path="header.apply_modal_cta" as="span" />
            </a>
          </div>
        </div>
      )}

      {scheduleTourModalOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="schedule-tour-overlay"
            onClick={closeScheduleTourModal}
            role="presentation"
          >
            <div
              className="schedule-tour-modal"
              role="dialog"
              aria-modal="true"
              aria-label="Schedule a Tour"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="schedule-tour-modal-header">
                <RichTextContent
                  path="header.schedule_tour_modal_title"
                  as="h2"
                  className="schedule-tour-modal-title"
                />
                <button
                  type="button"
                  className="schedule-tour-close"
                  onClick={closeScheduleTourModal}
                  aria-label="Close"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path d="M2 2L14 14M14 2L2 14" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
                  </svg>
                </button>
              </div>

              <div className="schedule-tour-embed">
                {scheduleTourEmbedUrl ? (
                  <iframe
                    key={scheduleTourEmbedUrl}
                    src={scheduleTourEmbedUrl}
                    title="Schedule a Tour"
                    className="schedule-tour-iframe"
                    allow="geolocation; microphone; camera; clipboard-write"
                  />
                ) : (
                  <div className="schedule-tour-empty">
                    <p>
                      Add a tour scheduler URL on the property custom field{' '}
                      <code>custom_mri_tour_scheduler_url</code> (or remapped via the
                      Schedule Tour Embed URL token) to show the booking form here.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
      <style>{`
        .dolben-header {
          font-family: var(--vibe-font-body);
        }

        /* Mobile: transparent over hero → primary fill on scroll / menu open.
           Desktop (lg+): always solid primary on the main row. */
        .header-main-row {
          background-color: transparent;
          transition: background-color 0.35s ease;
        }
        .header-mobile-solid .header-main-row {
          background-color: var(--vibe-color-primary);
        }
        @media (min-width: 1024px) {
          .header-main-row {
            background-color: var(--vibe-color-primary);
          }
        }

        /* Mobile call control — white rounded square + gray handset */
        .header-mobile-phone {
          border-radius: 10px;
          background-color: #ffffff;
          color: #8a8a8a;
          transition: opacity 0.2s ease, transform 0.2s ease;
        }
        .header-mobile-phone:hover {
          opacity: 0.9;
        }
        .header-mobile-phone:active {
          transform: scale(0.96);
        }

        /* Utility phone — plain weight, no uppercase (matches reference) */
        .header-util-phone {
          font-family: var(--vibe-font-body);
          font-size: 0.8125rem;
          font-weight: 400;
          letter-spacing: 0.01em;
          color: var(--vibe-color-text-on-secondary);
          text-decoration: none;
          white-space: nowrap;
          transition: opacity 0.2s ease;
        }
        .header-util-phone:hover {
          opacity: 0.8;
        }

        /* Utility links — uppercase, small, spaced */
        .header-util-link {
          display: inline-flex;
          align-items: center;
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          font-weight: 400;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--vibe-color-text-on-secondary);
          text-decoration: none;
          white-space: nowrap;
          transition: opacity 0.2s ease;
          background: none;
          border: none;
          padding: 0;
          cursor: pointer;
        }
        .header-util-link:hover {
          opacity: 0.8;
        }

        /* Main nav links — Title Case, underline draws L→R on hover/active */
        .dolben-nav-link {
          position: relative;
          text-decoration: none;
          transition: opacity 0.2s ease;
        }
        .dolben-nav-link::after {
          content: '';
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0.15em;
          height: 1px;
          background-color: var(--vibe-color-text-on-primary);
          transform: scaleX(0);
          transform-origin: left center;
          transition: transform 0.28s ease;
          pointer-events: none;
        }
        .dolben-nav-link:hover::after,
        .dolben-nav-link.is-active::after {
          transform: scaleX(1);
        }

        .nav-more-dropdown .dolben-nav-link::after {
          bottom: 0.55em;
        }

        /* Full-screen mobile menu — matches Ascent / 505 Sutton reference */
        .mobile-menu-phone {
          text-decoration: underline;
          text-underline-offset: 3px;
          text-decoration-thickness: 1px;
        }

        .mobile-menu-rule {
          height: 1px;
          width: 100%;
          background-color: color-mix(in srgb, var(--vibe-color-text-on-primary) 85%, transparent);
        }

        .mobile-nav-link {
          position: relative;
          display: inline-block;
          width: fit-content;
          text-decoration: none;
          transition: opacity 0.2s ease;
        }
        .mobile-nav-link::after {
          content: '';
          position: absolute;
          left: 0;
          right: 0;
          bottom: -0.2em;
          height: 2px;
          background-color: var(--vibe-color-text-on-primary);
          transform: scaleX(0);
          transform-origin: left center;
          transition: transform 0.28s ease;
          pointer-events: none;
        }
        .mobile-nav-link.is-active::after,
        .mobile-nav-link:hover::after {
          transform: scaleX(1);
        }
        .mobile-nav-link.is-child {
          margin-left: 1rem;
          opacity: 0.85;
          font-size: 0.95rem;
        }
        .mobile-nav-link:hover {
          opacity: 0.85;
        }

        .mobile-menu-cta {
          border-radius: 0;
        }

        .mobile-menu-hours {
          margin: 0;
        }
        .mobile-menu-hours dd {
          margin: 0;
        }

        /* Leave-site apply modal — matches partner-site interstitial */
        .apply-leave-overlay {
          position: fixed;
          inset: 0;
          z-index: 200;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1.25rem;
          background: rgba(0, 0, 0, 0.45);
        }

        .apply-leave-modal {
          position: relative;
          width: 100%;
          max-width: 520px;
          background: #ffffff;
          border: 1px solid #e0e0e0;
          border-radius: 0;
          padding: 2rem 2rem 1.75rem;
          box-shadow: none;
          font-family: var(--vibe-font-body);
        }

        .apply-leave-close {
          position: absolute;
          top: 0.75rem;
          right: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          padding: 0;
          border: none;
          border-radius: 0;
          background: transparent;
          color: #333333;
          cursor: pointer;
          line-height: 1;
        }
        .apply-leave-close:hover {
          opacity: 0.65;
        }

        .apply-leave-title {
          margin: 0 1.75rem 1.15rem 0;
          font-size: 1.125rem;
          font-weight: 700;
          line-height: 1.35;
          color: #333333;
          text-align: left;
        }

        .apply-leave-intro {
          margin: 0 0 0.65rem;
          font-size: 0.9375rem;
          font-weight: 400;
          line-height: 1.5;
          color: #606060;
        }

        .apply-leave-list {
          margin: 0 0 1rem;
          padding-left: 1.35rem;
          list-style: disc;
          color: #606060;
        }
        .apply-leave-list li {
          margin: 0 0 0.35rem;
          font-size: 0.9375rem;
          font-weight: 400;
          line-height: 1.5;
          color: #606060;
        }
        .apply-leave-list li::marker {
          color: #606060;
          font-size: 0.7em;
        }

        .apply-leave-closing {
          margin: 0 0 1.5rem;
          font-size: 0.9375rem;
          font-weight: 400;
          line-height: 1.5;
          color: #606060;
        }

        .apply-leave-cta {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          padding: 0.95rem 1.25rem;
          border-radius: 0;
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          font-weight: 400;
          line-height: 1.25;
          text-align: center;
          text-decoration: none;
          cursor: pointer;
        }

        /* Schedule a Tour embed popup — portaled to body; z-index above
           SpecialOfferBanner (90–100) and aligned with other site modals. */
        .schedule-tour-overlay {
          position: fixed;
          inset: 0;
          z-index: 220;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1.25rem;
          background: rgba(0, 0, 0, 0.55);
        }
        .schedule-tour-modal {
          position: relative;
          width: min(720px, 100%);
          max-height: min(90vh, 860px);
          display: flex;
          flex-direction: column;
          background: #ffffff;
          color: #1a1a1a;
          box-shadow: 0 24px 64px rgba(0, 0, 0, 0.28);
        }
        .schedule-tour-modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          padding: 1rem 1.25rem;
          border-bottom: 1px solid color-mix(in srgb, var(--vibe-color-text) 12%, transparent);
          flex-shrink: 0;
        }
        .schedule-tour-modal-title {
          margin: 0;
          font-family: var(--vibe-font-heading);
          font-size: 1.125rem;
          font-weight: 600;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          line-height: 1.3;
        }
        .schedule-tour-close {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 2rem;
          height: 2rem;
          padding: 0;
          border: none;
          background: transparent;
          color: #1a1a1a;
          cursor: pointer;
          opacity: 0.65;
          transition: opacity 0.2s ease;
          flex-shrink: 0;
        }
        .schedule-tour-close:hover {
          opacity: 1;
        }
        .schedule-tour-embed {
          position: relative;
          flex: 1 1 auto;
          min-height: min(70vh, 640px);
          background: #f5f5f5;
        }
        .schedule-tour-iframe {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          border: 0;
        }
        .schedule-tour-empty {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          min-height: inherit;
          padding: 2rem 1.5rem;
          text-align: center;
        }
        .schedule-tour-empty p {
          margin: 0;
          max-width: 28rem;
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          line-height: 1.55;
          color: var(--vibe-color-text-muted, #6b7280);
        }
        .schedule-tour-empty code {
          font-size: 0.85em;
          word-break: break-all;
        }
        @media (max-width: 639px) {
          .schedule-tour-overlay {
            padding: 0;
            align-items: stretch;
          }
          .schedule-tour-modal {
            width: 100%;
            max-height: none;
            height: 100%;
            border-radius: 0;
          }
          .schedule-tour-embed {
            min-height: 0;
          }
        }
      `}</style>
    </>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'Header',
  displayName: 'Header',
  tokens: {
    'header.phone': {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '(555) 555-5555',
    },
    'header.interactive_map_show_if_exists': {
      label: 'Interactive Map: Show If Exists',
      type: 'text',
      description:
        'Expose the Interactive Map page in the navigation if this token or field contains a value (e.g. custom_engrain_sightmap_url). Leave blank to hide.',
      defaultValue: '',
    },
    'header.apply_modal_title': {
      label: 'Apply Modal Title',
      type: 'richtext',
      defaultValue:
        "<p>You're about to leave the site to begin the application process on one of our trusted partner sites.</p>",
    },
    'header.apply_modal_intro': {
      label: 'Apply Modal Intro',
      type: 'richtext',
      defaultValue: '<p>During the application process you may:</p>',
    },
    'header.apply_modal_bullet_1': {
      label: 'Apply Modal Bullet 1',
      type: 'richtext',
      defaultValue: '<p>See pricing and charge amounts displayed in different format.</p>',
    },
    'header.apply_modal_bullet_2': {
      label: 'Apply Modal Bullet 2',
      type: 'richtext',
      defaultValue: '<p>Need to re-enter selections you made in the price calculator.</p>',
    },
    'header.apply_modal_closing': {
      label: 'Apply Modal Closing',
      type: 'richtext',
      defaultValue:
        '<p>Thank you for your understanding while we enhance our system capabilities.</p>',
    },
    'header.apply_modal_cta': {
      label: 'Apply Modal CTA',
      type: 'richtext',
      defaultValue: '<p>Accept &amp; Continue</p>',
    },
    'header.schedule_tour_embed_url': {
      label: 'Schedule Tour Embed URL',
      type: 'url',
      defaultField: 'custom_mri_tour_scheduler_url',
      description:
        'Iframe URL for the Schedule a Tour popup. Bound to custom_mri_tour_scheduler_url by default.',
    },
    'header.schedule_tour_modal_title': {
      label: 'Schedule Tour Modal Title',
      type: 'richtext',
      defaultValue: '<p>Schedule a Tour</p>',
    },
  },
  slotGroups: {
    'header.logo': {
      label: 'Logo',
      fields: {
        image: { label: 'Logo Image', type: 'image', defaultField: 'property_primary_logo', suggestedFields: ['property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
      },
    },
    'header.apply_now_link': {
      label: 'Apply Now Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_apply_now_url',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.resident_access_link': {
      label: 'Resident Login Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Login' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
  },
  collectionBindings: {
    'header.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
    'header.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Header as any).contentSchema = contentSchema;

export default Header;
